/**
 * Anchored VWAP utilities.
 *
 * Anchored VWAP = Σ(typical_price × volume) / Σ(volume), accumulated bar-by-bar
 * from the FIRST bar of the selected period (the "anchor") through each
 * subsequent bar. The line visibly rises and falls as price + volume evolve —
 * unlike a flat horizontal reference, this is a real time series.
 *
 * Required per-bar fields: { price OR close, high, low, volume }
 *   (high/low optional — falls back to close for typical_price)
 */

/**
 * Returns a NEW array (chart_data clone) with a `vwap` field on every bar.
 * Bars without usable volume reuse the previous cumulative VWAP so the line
 * stays connected (instead of dropping to null).
 */
export function enrichWithAnchoredVwap(chartData) {
  if (!Array.isArray(chartData) || chartData.length === 0) return [];
  let cumPV = 0;
  let cumV = 0;
  let lastVwap = null;
  return chartData.map((bar) => {
    const close = bar.close ?? bar.price;
    if (close == null) {
      return { ...bar, vwap: lastVwap };
    }
    const high = bar.high ?? close;
    const low = bar.low ?? close;
    const vol = Number(bar.volume) || 0;
    const tp = (high + low + close) / 3;
    if (vol > 0 && tp > 0) {
      cumPV += tp * vol;
      cumV += vol;
    }
    const vwap = cumV > 0 ? cumPV / cumV : tp;
    lastVwap = Number(vwap.toFixed(2));
    return { ...bar, vwap: lastVwap };
  });
}

/** Latest (final) anchored VWAP value from an enriched array. */
export function latestAnchoredVwap(enriched) {
  if (!Array.isArray(enriched) || enriched.length === 0) return null;
  for (let i = enriched.length - 1; i >= 0; i--) {
    if (enriched[i].vwap != null) return enriched[i].vwap;
  }
  return null;
}
